import { EditOutlined, LockOutlined } from '@mui/icons-material';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import { SectionCard } from 'components/v2/common/sectionCard';

import api, { type Application } from 'api';
import { getClientStatusLabelKey } from 'helpers/applicationRetainers';
import useEnabledCountries from 'hooks/useEnabledCountries';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadPrograms } from 'redux/programs/programs.middleware';
import type { AppDispatch, RootState } from 'redux/store';
import type { CaseProgramFormInterface } from 'types';
import { chosenCountryName, countryNameOptions } from 'utils/countries';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { caseProgramValidationSchema } from 'utils/validations/applicationRetainers';

interface CaseSectionProps {
    application: Application;
    /** A signed visa-application freezes program/country (API returns 409 on change). */
    locked: boolean;
    onSaved: () => void;
    extraActions?: React.ReactNode;
    children?: React.ReactNode;
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}
        >
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
            {value || '—'}
        </Typography>
    </Box>
);

export const CaseSection: React.FC<CaseSectionProps> = ({
    application,
    locked,
    onSaved,
    extraActions,
    children,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const programs = useSelector((state: RootState) => state.programs.list);
    const programsLoading = useSelector((state: RootState) => state.programs.isLoading);

    const [editing, setEditing] = useState(false);
    const [country, setCountry] = useState(application.country || '');

    useEffect(() => {
        setEditing(false);
        setCountry(application.country || '');
    }, [application.id, application.country]);

    useEffect(() => {
        if (editing && country && application.candidate_id) {
            void dispatch(loadPrograms({ userId: application.candidate_id, country }));
        }
    }, [application.candidate_id, country, dispatch, editing]);

    const { codes } = useEnabledCountries();
    const countryOptions = useMemo(
        () =>
            countryNameOptions(codes, application.country).map((name) => ({
                label: name,
                value: name,
            })),
        [application.country, codes]
    );

    const programOptions = useMemo(() => {
        const options = programs.map((program) => ({
            label: program.label,
            value: program.value,
        }));
        // Keep the saved program selectable even when the list has not loaded it
        if (
            application.program_name &&
            !options.some((option) => option.value === application.program_name)
        ) {
            options.unshift({ label: application.program_name, value: application.program_name });
        }
        return options;
    }, [application.program_name, programs]);

    const clientStatusKey = getClientStatusLabelKey(application.client_status);

    const handleSubmit = async (values: CaseProgramFormInterface) => {
        try {
            await api.application.patch(String(application.id), {
                country: values.country,
                program_name: values.programName,
            });
            dispatch(setAlert({ message: translate('caseUpdated'), type: 'success' }));
            setEditing(false);
            onSaved();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const initialValues: CaseProgramFormInterface = {
        country: chosenCountryName(codes, application.country) ?? '',
        programName: application.program_name || '',
    };

    return (
        <SectionCard
            title={translate('retainerStepCase')}
            actions={
                <>
                    {locked ? (
                        <Tooltip title={translate('caseLockedDescription')}>
                            <Chip
                                size="small"
                                icon={<LockOutlined sx={{ fontSize: 16 }} />}
                                label={translate('locked')}
                                variant="outlined"
                            />
                        </Tooltip>
                    ) : !editing ? (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlined />}
                            onClick={() => setEditing(true)}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('edit')}
                        </Button>
                    ) : null}
                    {extraActions}
                </>
            }
        >
            {editing && !locked ? (
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={caseProgramValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, setFieldValue }) => (
                        <Form>
                            <FormGridLayout>
                                <FormField
                                    name="country"
                                    label="country"
                                    type="select"
                                    options={countryOptions}
                                    disabled={isSubmitting}
                                    onChange={(value) => {
                                        setCountry(String(value ?? ''));
                                        void setFieldValue('programName', '');
                                    }}
                                />
                                <FormField
                                    name="programName"
                                    label="program"
                                    type="select"
                                    options={programOptions}
                                    disabled={isSubmitting || programsLoading}
                                />
                            </FormGridLayout>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 2, justifyContent: 'flex-end' }}
                            >
                                <Button
                                    variant="outlined"
                                    disabled={isSubmitting}
                                    onClick={() => setEditing(false)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {translate('cancel')}
                                </Button>
                                <SubmitButton loading={isSubmitting}>save</SubmitButton>
                            </Stack>
                        </Form>
                    )}
                </Formik>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                        gap: 2,
                    }}
                >
                    <Field label={translate('program')} value={application.program_name} />
                    <Field label={translate('country')} value={application.country} />
                    <Field
                        label={translate('clientStatus')}
                        value={
                            clientStatusKey ? translate(clientStatusKey) : application.client_status
                        }
                    />
                    <Field label={translate('consultant')} value={application.consultant_name} />
                </Box>
            )}
            {children}
        </SectionCard>
    );
};
